// Help for the routes that start from your own runs (B: interface files, C: a finished Wannier90 model) with input
// fragments to copy, and the teaching notes: what each step does to the Wannier functions, on the user's own data.
import { Accordion, Alert, Code, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";

const QE_STEPS = `# 1 the SCF as usual, then an nscf on the full uniform mesh (no symmetry)
kmesh.pl 6 6 6 > kpts.txt          # Wannier90's utility: the k list, same order as mp_grid
pw.x -in nscf.in > nscf.out        # calculation='nscf', nosym=.true., noinv=.true., nbnd=<num_bands>, K_POINTS crystal (kpts.txt)
# 2 the overlaps and projections
wannier90.x -pp seed               # reads seed.win, writes seed.nnkp
pw2wannier90.x -in seed.pw2wan     # writes seed.amn, seed.mmn, seed.eig
# 3 upload seed.win, seed.amn, seed.mmn, seed.eig here (gzip them: they get smaller)`;

const PW2WAN = `&inputpp
  outdir   = './out'
  prefix   = 'pwscf'
  seedname = 'seed'
  write_amn = .true.
  write_mmn = .true.
  write_unk = .false.
/`;

const WIN = `num_wann  = 8
num_bands = 12
dis_win_min  = -10.0     ! eV, absolute (not relative to E_F)
dis_win_max  =  18.0
dis_froz_min = -10.0
dis_froz_max =   7.0
begin projections
Si:sp3
end projections
mp_grid = 6 6 6
begin unit_cell_cart … end unit_cell_cart
begin atoms_frac … end atoms_frac
begin kpoints … end kpoints        ! the kmesh.pl list`;

const VASP = `LWANNIER90 = .TRUE.          ! INCAR: VASP writes wannier90.win, .amn, .mmn, .eig
NUM_WANN   = 8
WANNIER90_WIN = "
begin projections
Si:sp3
end projections
dis_win_max = 18.0
dis_froz_max = 7.0
"
ISYM = -1                    ! a full k-mesh (Γ-centred, as in mp_grid)`;

const ROUTE_C = `write_hr  = .true.          ! seed_hr.dat: H(R)
write_xyz = .true.          ! seed_centres.xyz: the centres (needed without the .wout)
use_ws_distance = .true.    ! seed_wsvec.dat: the shortest lattice vector of each hopping`;

export function RouteBHelp() {
  return (
    <Accordion variant="contained" chevronPosition="left" data-testid="route-b-help">
      <Accordion.Item value="qe">
        <Accordion.Control><Text size="sm">How to make these files with Quantum ESPRESSO</Text></Accordion.Control>
        <Accordion.Panel>
          <Stack gap={6}>
            <Code block>{QE_STEPS}</Code>
            <Text size="xs" c="dimmed">seed.pw2wan:</Text>
            <Code block>{PW2WAN}</Code>
            <Text size="xs" c="dimmed">The essentials of seed.win (the windows here are absolute energies, in eV):</Text>
            <Code block>{WIN}</Code>
            <Text size="xs" c="dimmed">Easier: run the DFT in a DFT project here and choose "From a DFT run" - the server does all of this.</Text>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
      <Accordion.Item value="vasp">
        <Accordion.Control><Text size="sm">How to make these files with VASP</Text></Accordion.Control>
        <Accordion.Panel>
          <Stack gap={6}>
            <Code block>{VASP}</Code>
            <Text size="xs" c="dimmed">A non-self-consistent run from the CHGCAR (ICHARG = 11) on the uniform mesh is enough. Upload wannier90.win,
              .amn, .mmn and .eig, and give the Fermi energy of the SCF run (OUTCAR, E-fermi).</Text>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export function RouteCHelp() {
  return (
    <Accordion variant="contained" chevronPosition="left" data-testid="route-c-help">
      <Accordion.Item value="c">
        <Accordion.Control><Text size="sm">Which Wannier90 output this needs</Text></Accordion.Control>
        <Accordion.Panel>
          <Stack gap={6}>
            <Text size="xs">In the .win of your Wannier90 run:</Text>
            <Code block>{ROUTE_C}</Code>
            <Text size="xs" c="dimmed">Then upload seed_hr.dat with seed.wout (it holds the cell, the atoms and the centres) - or with seed.win and
              seed_centres.xyz - and seed_wsvec.dat if you have it. A collinear magnet: one _hr.dat per spin.</Text>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

/** What each step does, for teaching (shown with "Teaching notes" on). */
export const TEACHING: Record<string, ReactNode> = {
  source: <>A Wannier function is a Fourier transform of Bloch states over the Brillouin zone: w_n(r − R) = (V/(2π)³) ∫ dk e^(−ik·R)
    Σ_m U_mn(k) ψ_mk(r). The DFT run gives the ψ_mk; Wannier90 chooses the unitary mixing U(k). Any U(k) gives the same bands - the
    <b> gauge freedom</b> - but different functions in real space.</>,
  mesh: <>The integral over k becomes a sum over the uniform mesh: N₁ × N₂ × N₃ k-points give Wannier functions that repeat every
    N₁ × N₂ × N₃ cells (the Born-von Kármán supercell). Hoppings longer than that supercell are folded back, which is why a coarse mesh
    shows up as hoppings at the supercell's edge and as errors between the mesh points.</>,
  projections: <>The projections are the starting guess: Wannier90 projects the Bloch states onto these trial orbitals, A_mn(k) =
    ⟨ψ_mk|g_n⟩, and starts from there. Hybrids (sp³) give bond-centred functions, single orbitals (p_z) atom-centred ones. A poor guess
    can end in a poor local minimum of the spread.</>,
  windows: <>With more bands than functions, the subspace itself is chosen (<b>disentanglement</b>, Souza-Marzari-Vanderbilt): at every
    k Wannier90 picks the num_wann-dimensional subspace of the outer window that changes least from k to its neighbours - minimising
    the gauge-invariant spread Ω_I. States inside the frozen window are always kept, so the bands there are reproduced exactly on the
    mesh.</>,
  run: <>After the disentanglement, the localisation minimises the rest of the spread, Ω_D + Ω_OD, by rotating within the chosen
    subspace (Marzari-Vanderbilt). Watch the spread fall while the run goes; a curve that is still falling at the last iteration has not
    converged (restart with more iterations - the .chk keeps everything).</>,
  results: <>Check the model three ways: its bands against DFT (exact on the mesh in the frozen window; between mesh points the
    interpolation), the spreads and centres (compact functions where you expect them: on atoms or on bonds), and the decay of H(R) (fast
    decay means a short-ranged, transferable model). H(R) of a model without spin-orbit coupling should be real.</>,
};

export function TeachNote({ step, on }: { step: keyof typeof TEACHING | string; on: boolean }) {
  if (!on || !TEACHING[step]) return null;
  return <Alert color="indigo" variant="light" p="xs" title="Teaching note" data-testid={`teach-${step}`}><Text size="sm">{TEACHING[step]}</Text></Alert>;
}
